/**
 * The HTML a `RichTextEditor` produces, made safe to paint again - and kept
 * to what the backend will keep.
 *
 * The editor is a `contenteditable` box, and what it hands back is whatever
 * markup the browser built for the formatting that was applied - which is
 * also whatever a paste from another page brought with it. Everything drawn
 * from that string goes through `sanitizeHtml` first: the live preview beside
 * the editor, and the editor's own box when a stored copy is loaded into it.
 *
 * The allow-list is the backend's, to the letter - `apps/common/sanitize.py`,
 * as the Guidelines & policy guide sets it out. The server strips anything
 * outside it on the way in and does not say so, so a bar that offered more
 * would be offering formatting that vanishes on Save. Keeping the same list
 * here means the preview shows what will actually be kept, and the editor
 * never lets a mark be made that the server would take off again.
 *
 * What is kept: the four headings, paragraphs, the three lists tags, bold
 * and italic in either spelling, underline, a line break, and a `<span>`
 * carrying a `color` or a `background-color` - and nothing else. No
 * attribute other than that one `style`, no link, no image, no size, no
 * alignment, no strikethrough.
 */

const ALLOWED_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "p",
  "ul",
  "ol",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "span",
  "br",
]);

/** The only two declarations a `<span>` may carry. */
const ALLOWED_STYLES = new Set(["color", "background-color"]);

/**
 * A colour as the server accepts one: a 3- or 6-digit hex, or an `rgb()` /
 * `rgba()` triple. A named colour - `red`, `teal` - is refused and dropped,
 * and so is `transparent`, which is what "no highlight" is written as and
 * means the same thing gone.
 */
const COLOUR_VALUE =
  /^(#[0-9a-f]{3}|#[0-9a-f]{6}|rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\))$/i;

/** Anything that runs or embeds goes with everything inside it. */
const DROPPED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "img",
  "svg",
]);

const cleanStyle = (style: string): string =>
  style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const colon = declaration.indexOf(":");
      if (colon < 0) return false;
      const name = declaration.slice(0, colon).trim().toLowerCase();
      const value = declaration.slice(colon + 1).trim();
      return ALLOWED_STYLES.has(name) && COLOUR_VALUE.test(value);
    })
    .join("; ");

/**
 * Every attribute off, bar a `<span>`'s `style` - and that pared to the two
 * colours. The allow-list grants `style` to nothing else: a coloured heading
 * is a `<span>` inside the heading, never a `style` on the heading tag.
 */
const cleanElement = (element: Element) => {
  const tag = element.tagName.toLowerCase();

  // The list is copied first: removing while walking `attributes` skips the
  // one after each.
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (tag === "span" && name === "style") {
      const kept = cleanStyle(attribute.value);
      if (kept) element.setAttribute("style", kept);
      else element.removeAttribute("style");
    } else {
      element.removeAttribute(attribute.name);
    }
  }
};

/**
 * The two shapes a browser writes that the server would unwrap but that
 * carry something worth keeping, rewritten into the allowed shape instead:
 * a `<div>` is a block and becomes a `<p>`, so two lines stay two lines; a
 * `<font color>` - what an engine that ignores `styleWithCSS` writes for a
 * colour - becomes the `<span>` the same colour is meant to be written as.
 */
const rewrite = (element: Element): Element | null => {
  const tag = element.tagName.toLowerCase();
  const doc = element.ownerDocument;

  if (tag === "div") {
    const p = doc.createElement("p");
    while (element.firstChild) p.appendChild(element.firstChild);
    element.replaceWith(p);
    return p;
  }

  if (tag === "font" && element.getAttribute("color")) {
    const span = doc.createElement("span");
    span.setAttribute("style", `color: ${element.getAttribute("color")}`);
    while (element.firstChild) span.appendChild(element.firstChild);
    element.replaceWith(span);
    return span;
  }

  return null;
};

const walk = (node: Node) => {
  // Snapshot again, for the same reason as the attributes: an unwrapped
  // element replaces itself with its children mid-walk.
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.parentNode?.removeChild(child);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    let element = child as Element;
    let tag = element.tagName.toLowerCase();

    if (DROPPED_TAGS.has(tag)) {
      element.parentNode?.removeChild(element);
      continue;
    }

    const rewritten = rewrite(element);
    if (rewritten) {
      element = rewritten;
      tag = element.tagName.toLowerCase();
    }

    // Anything merely unknown - a `<table>`, a `<section>`, a link - is
    // unwrapped so the words in it are kept while the box around them is
    // not. The same thing the server does with it.
    if (!ALLOWED_TAGS.has(tag)) {
      walk(element);
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) parent.insertBefore(element.firstChild, element);
        parent.removeChild(element);
      }
      continue;
    }

    cleanElement(element);
    walk(element);
  }
};

/**
 * The markup with everything outside the allow-list removed. Answers the
 * input untouched where there is no DOM to parse with - a test running
 * outside a browser - which is where nothing is painted anyway.
 */
export const sanitizeHtml = (html: string): string => {
  if (typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  walk(doc.body);
  return doc.body.innerHTML;
};

/**
 * Whether there are any words in it. A box that has been typed into and
 * cleared is left holding a `<p><br></p>` or a bare `<br>` by the browser,
 * which is not something worth saving or worth previewing.
 */
export const isRichTextEmpty = (html: string): boolean => {
  if (typeof DOMParser === "undefined") return html.trim() === "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").trim() === "";
};

/**
 * Whether two pieces of markup say the same thing.
 *
 * Not a bare string comparison, because the browser re-serialises what it is
 * given: an `&` in the text comes back as `&amp;`, attribute quotes are
 * normalised, a stray tag is closed. A stored copy compared byte for byte
 * against the box that was just loaded from it could read as edited before
 * anyone had typed. Both sides go through the same parse, so they are
 * compared as the browser would write them out.
 */
export const sameRichText = (a: string, b: string): boolean =>
  a === b || sanitizeHtml(a) === sanitizeHtml(b);
